import { useEffect, useState } from 'react';

import { supabase } from '../../lib/supabase';
import { BOAS_VINDAS } from '../../constants/landing';
import * as membersDb from '../../services/db/members';
import type { Ir } from '../porta';

// AS BOAS-VINDAS DEPOIS DO CADASTRO — a lógica do antigo `pages/Welcome`, sem o desenho.
//
// Quem chega aqui pode ser duas pessoas diferentes, e mandar as duas para o mesmo lugar confunde
// justamente quem tem menos contexto:
//   • artista sem perfil nenhum  → criar o primeiro perfil;
//   • convidado para a equipe de alguém → ver e aceitar o convite.
// O convite pendente NÃO conta como perfil (`get_shared_artist_ids_v2` só devolve membros
// ativos). A copy vive no núcleo: o app tem a mesma tela, com as mesmas palavras.
// Mora no núcleo desde 01/11/2026 (docs/PLANO-APP-NATIVO.md): a web e o app usam a mesma regra.

type Chegada = { rota: string; convidado: boolean };

export const useBoasVindas = ({ ir: navigate }: { ir: Ir }) => {
  const [chegada, setChegada] = useState<Chegada | null>(null);

  useEffect(() => {
    let vivo = true;
    const decidir = (c: Chegada) => { if (vivo) setChegada(c); };

    Promise.all([
      // Sem filtro por user_id: a RLS de `artists` devolve os próprios e os compartilhados em que a
      // pessoa vê o Desenvolvimento. O vínculo ativo cobre o perfil compartilhado sem esse acesso,
      // cuja linha crua a RLS esconde (decisão D1 da auditoria da Nyta, 04/10/2026).
      supabase.from('artists').select('id', { count: 'exact', head: true }),
      membersDb.fetchPendingInvites().catch(() => []),
      supabase.from('artist_members').select('id', { count: 'exact', head: true }).eq('status', 'active'),
    ])
      .then(([perfis, convites, vinculos]) => {
        const temPerfil = (!perfis.error && (perfis.count ?? 0) > 0) || (!vinculos.error && (vinculos.count ?? 0) > 0);
        const temConvite = convites.length > 0;
        decidir({
          rota: temPerfil || temConvite ? '/artists' : '/criar-artista',
          convidado: !temPerfil && temConvite,
        });
      })
      // Na falha, a lista de perfis é o destino seguro: lida com os dois casos e mostra o convite.
      .catch(() => decidir({ rota: '/artists', convidado: false }));

    return () => { vivo = false; };
  }, []);

  return {
    /** Enquanto não se sabe quem chegou, nada é dito (a frase não pode trocar na frente da pessoa). */
    pronto: chegada !== null,
    saudacao: chegada?.convidado ? BOAS_VINDAS.convidado : BOAS_VINDAS.artista,
    botao: chegada?.convidado ? BOAS_VINDAS.botaoDoConvite : BOAS_VINDAS.botao,
    comecar: () => navigate(chegada?.rota || '/artists', { replace: true }),
  };
};
